import { STEP1_COVERAGE_TOPICS, CoverageTopic, coverageTopicLabel } from '../config/step1CoverageTopics';
import { STEP1_LIFE_STORY_PROMPTS } from '../config/step1LifeStoryPrompts';
import { CoverageTopicStatus, Step1WizardAnswerEntry } from '../types/step1Writing';

function hasSubstantiveText(examples: string[] | undefined): boolean {
  if (!examples?.length) return false;
  return examples.some((e) => e.trim().length > 10);
}

function inferFromWizardAnswers(
  wizardAnswers: Record<string, Step1WizardAnswerEntry>
): Record<string, CoverageTopicStatus> {
  const status: Record<string, CoverageTopicStatus> = {};
  for (const topic of STEP1_COVERAGE_TOPICS) {
    status[topic.id] = 'not_yet';
  }

  for (const prompt of STEP1_LIFE_STORY_PROMPTS) {
    const entry = wizardAnswers[prompt.id];
    if (!entry) continue;
    const st = entry.status;
    for (const topicId of prompt.coverageTopicIds) {
      if (st === 'na' || st === 'covered_elsewhere') {
        status[topicId] = st;
      } else if (st === 'skipped') {
        if (status[topicId] === 'not_yet') status[topicId] = 'partial';
      } else if (hasSubstantiveText(entry.examples)) {
        status[topicId] = 'addressed';
      } else if (entry.examples?.some((e) => e.trim())) {
        if (status[topicId] !== 'addressed') status[topicId] = 'partial';
      }
    }
  }
  return status;
}

export function mergeCoverageStatus(
  wizardAnswers: Record<string, Step1WizardAnswerEntry>,
  manualMarks: Record<string, CoverageTopicStatus> | undefined,
  contentText: string | undefined
): Record<string, CoverageTopicStatus> {
  const fromWizard = inferFromWizardAnswers(wizardAnswers);
  const merged = { ...fromWizard };

  if (manualMarks) {
    for (const [k, v] of Object.entries(manualMarks)) {
      if (v && v !== 'not_yet') merged[k] = v;
    }
  }

  // Typed/upload content: mark introduction & closing addressed if template-like content exists
  const text = (contentText || '').trim();
  if (text.length > 100) {
    if (merged.introduction === 'not_yet') merged.introduction = 'partial';
    if (merged.closing === 'not_yet') merged.closing = 'partial';
    for (const topic of STEP1_COVERAGE_TOPICS) {
      const keywords = topicKeywords(topic);
      if (keywords.some((kw) => text.toLowerCase().includes(kw))) {
        if (merged[topic.id] === 'not_yet') merged[topic.id] = 'partial';
      }
    }
  }

  return merged;
}

function topicKeywords(topic: CoverageTopic): string[] {
  const base = topic.label.toLowerCase().split(/\s+/).slice(0, 3);
  switch (topic.id) {
    case 'powerlessness':
      return ['powerless', 'could not stop', 'couldn\'t stop'];
    case 'unmanageability':
      return ['unmanageable', 'chaos', 'damage'];
    case 'bottom':
      return ['bottom', 'turning point'];
    default:
      return base;
  }
}

export function coverageSummaryCounts(status: Record<string, CoverageTopicStatus>): {
  addressed: number;
  partial: number;
  notYet: number;
  total: number;
} {
  const values = STEP1_COVERAGE_TOPICS.map((t) => status[t.id] || 'not_yet');
  return {
    addressed: values.filter((v) => v === 'addressed' || v === 'na' || v === 'covered_elsewhere').length,
    partial: values.filter((v) => v === 'partial').length,
    notYet: values.filter((v) => v === 'not_yet').length,
    total: STEP1_COVERAGE_TOPICS.length,
  };
}

export function uncoveredTopicLabels(status: Record<string, CoverageTopicStatus>): string[] {
  return STEP1_COVERAGE_TOPICS.filter((t) => {
    const s = status[t.id];
    return !s || s === 'not_yet' || s === 'partial';
  }).map((topic) => coverageTopicLabel(topic));
}
